import React from "react";
import {Box} from "@mui/material";
import {printoutPalette} from "../../core/printoutPalette";
import {printoutRule, printoutTheme, printoutTypeSx} from "../../core/printoutTheme";
import {
    FOLIO_INCLUSIVE_LABEL,
    FOLIO_PROTECTED_TOTAL_KEY,
} from "./printoutFolioTableOptions";
import type {
    FolioAlign,
    FolioColumn,
    FolioResolvedSection,
    FolioTextStyle,
    FolioTotal,
} from "./printoutFolioTableOptions";

// Text columns share the remaining width.
const FLEXIBLE_COLUMN_KEYS = new Set([
    "folioItemDescription",
    "folioItemPriceDescription",
    "folioItemCategory",
]);

/*
    Value columns keep fixed tracks so every row lines up with the header. Each track fits the widest real
    value or header noted beside it, at the table's 12px font in German and English and in the default
    fonts. Anything wider still, such as a long code, wraps inside its own track.
*/
const FIXED_COLUMN_WIDTHS: Record<string, string> = {
    folioItemCode: "72px", // an 8-letter code
    folioItemDiscount: "88px", // "-€ 5,00, -10 %"
    folioItemDuration: "96px", // "10 080 Minuten", a week-long line
    folioItemEndDate: "84px", // "29.09.2026"
    folioItemEndTime: "62px", // the "End time" header
    folioItemInclusive: "64px", // the "Inclusive" header
    folioItemPrice: "88px", // "US$12,345.67"
    folioItemQuantity: "56px", // the "Quantity" header
    folioItemStartDate: "84px", // "29.09.2026"
    folioItemStartTime: "62px", // the "Start time" header
    folioItemTaxRate: "96px", // "VAT 20 % exkl."
    folioItemTotal: "92px", // "€ 123.456,78" in bold
    folioItemTotalBeforeTax: "92px", // "€ 123.456,78"
};

const getColumnTrack = (column: FolioColumn) => {
    if (FLEXIBLE_COLUMN_KEYS.has(column.variableKey)) return "minmax(0, 1fr)";

    return FIXED_COLUMN_WIDTHS[column.variableKey] ?? "minmax(64px, 0.7fr)";
};

const cellJustify = (align: FolioAlign) => (
    align === "right" ? "flex-end" : align === "center" ? "center" : "flex-start"
);

// Bold is additive so base header/total weights remain intact.
const formatSx = (style: FolioTextStyle) => ({
    fontStyle: style.italic ? "italic" : "normal",
    ...(style.bold ? {fontWeight: 700} : {}),
});

export interface FolioTableSection extends Omit<FolioResolvedSection, "label"> {
    label: React.ReactNode;
}

interface Props {
    columns: FolioColumn[];
    totals: FolioTotal[];
    rowCount: number;
    renderCell: (column: FolioColumn, rowIndex: number) => React.ReactNode;
    renderTotalValue: (total: FolioTotal) => React.ReactNode;
    sections?: FolioTableSection[] | null;
    inclusiveRowIndexes?: number[] | null;
}

const FolioTableView = ({
    columns,
    inclusiveRowIndexes,
    renderCell,
    renderTotalValue,
    rowCount,
    sections,
    totals,
}: Props) => {
    const gridTemplateColumns = columns.map(getColumnTrack).join(" ");
    const columnGap = `${printoutTheme.table.columnGap}px`;
    const inclusiveRows = new Set(inclusiveRowIndexes ?? []);
    const renderedSections: FolioTableSection[] = sections?.length
        ? sections
        : [{label: "", rowCount, startIndex: 0}];
    const grandTotalIndex = totals.findIndex(total => total.variableKey === FOLIO_PROTECTED_TOTAL_KEY);
    const inclusiveColumnId = columns.find(column => FLEXIBLE_COLUMN_KEYS.has(column.variableKey))?.id
        ?? columns[0]?.id;

    const renderRow = (rowIndex: number) => (
        <Box
            className="printout-folio-row"
            key={rowIndex}
            sx={{
                alignItems: "baseline",
                borderBottom: printoutRule("light"),
                columnGap,
                display: "grid",
                fontVariantNumeric: "tabular-nums",
                ...printoutTypeSx(printoutTheme.type.tableValue),
                gridTemplateColumns,
                py: `${printoutTheme.table.rowPaddingY}px`,
            }}
        >
            {columns.map(column => (
                <Box
                    key={column.id}
                    sx={{
                        color: column.cellFormat.bold ? printoutPalette.primaryText : undefined,
                        display: "flex",
                        gap: "6px",
                        justifyContent: cellJustify(column.align),
                        minWidth: 0,
                        // A value too wide for its track wraps within it instead of painting over the next column.
                        overflowWrap: "anywhere",
                        textAlign: column.align,
                        ...formatSx(column.cellFormat),
                    }}
                >
                    {renderCell(column, rowIndex)}
                    {column.id === inclusiveColumnId && inclusiveRows.has(rowIndex) ? (
                        <Box component="span" sx={{color: printoutPalette.mutedText, fontWeight: 400, whiteSpace: "nowrap"}}>
                            {FOLIO_INCLUSIVE_LABEL}
                        </Box>
                    ) : null}
                </Box>
            ))}
        </Box>
    );

    return (
        <Box sx={{color: printoutPalette.secondaryText, display: "flex", flexDirection: "column", width: "100%"}}>
            <Box
                className="printout-folio-header"
                sx={{
                    alignItems: "end",
                    borderBottom: printoutRule("default"),
                    color: printoutPalette.mutedText,
                    columnGap,
                    display: "grid",
                    ...printoutTypeSx(printoutTheme.type.label),
                    gridTemplateColumns,
                    pb: `${printoutTheme.table.headerPaddingBottom}px`,
                }}
            >
                {columns.map(column => (
                    <Box
                        key={column.id}
                        sx={{
                            display: "flex",
                            justifyContent: cellJustify(column.align),
                            minWidth: 0,
                            overflowWrap: "anywhere",
                            textAlign: column.align,
                            ...formatSx(column.headerFormat),
                        }}
                    >
                        {column.label || column.variableKey}
                    </Box>
                ))}
            </Box>

            {renderedSections.map((section, sectionIndex) => (
                <React.Fragment key={section.startIndex}>
                    {section.label ? (
                        // Keep a day header with the first row in print.
                        <Box
                            className="printout-folio-group-header"
                            sx={{
                                color: printoutPalette.primaryText,
                                ...printoutTypeSx(printoutTheme.type.label),
                                pb: `${printoutTheme.table.groupHeaderPaddingBottom}px`,
                                pt: `${sectionIndex === 0 ? printoutTheme.table.groupHeaderPaddingBottom : printoutTheme.table.groupHeaderPaddingTop}px`,
                            }}
                        >
                            {section.label}
                        </Box>
                    ) : null}
                    {Array.from({length: section.rowCount}).map((_, offset) => renderRow(section.startIndex + offset))}
                </React.Fragment>
            ))}

            {totals.length > 0 && (
                <Box
                    className="printout-folio-totals"
                    sx={{
                        display: "flex",
                        flexDirection: "column",
                        ml: "auto",
                        mt: `${printoutTheme.table.totalsMarginTop}px`,
                        width: printoutTheme.table.totalsWidth,
                    }}
                >
                    {totals.map((total, index) => {
                        const isGrandTotal = index === grandTotalIndex;
                        const isSettlement = grandTotalIndex >= 0 && index > grandTotalIndex;
                        const step = isGrandTotal ? printoutTheme.grandTotal : printoutTheme.settledTotal;
                        const takesRule = isGrandTotal || index === grandTotalIndex + 1;

                        return (
                            <Box
                                className="printout-folio-total-row"
                                key={total.id}
                                sx={{
                                    alignItems: "baseline",
                                    color: (isGrandTotal || isSettlement || total.format.bold)
                                        ? printoutPalette.primaryText
                                        : printoutPalette.secondaryText,
                                    columnGap: "12px",
                                    display: "grid",
                                    fontSize: `${printoutTheme.type.body2.fontSize}px`,
                                    gridTemplateColumns: "minmax(0, 1fr) auto",
                                    lineHeight: printoutTheme.type.body2.lineHeight,
                                    py: `${printoutTheme.table.totalsRowPaddingY}px`,
                                    ...((isGrandTotal || isSettlement) ? {
                                        fontSize: `${step.fontSize}px`,
                                        fontWeight: step.fontWeight,
                                        ...(step.letterSpacing ? {letterSpacing: step.letterSpacing} : {}),
                                    } : {}),
                                    ...(((isGrandTotal || isSettlement) && takesRule) ? {
                                        borderTop: printoutRule(step.ruleTone),
                                        mt: `${step.marginTop}px`,
                                        pt: `${step.paddingTop}px`,
                                    } : {}),
                                    ...formatSx(total.format),
                                }}
                            >
                                <Box sx={{textAlign: "right"}}>{total.label}</Box>
                                <Box sx={{textAlign: "right"}}>{renderTotalValue(total)}</Box>
                            </Box>
                        );
                    })}
                </Box>
            )}
        </Box>
    );
};

export default FolioTableView;
